import React from 'react';

export const RECOVERY_DEFAULTS = {
  enabled: false,
  primary_model: 'claude-sonnet-5-5',
  recovery_model: 'claude-opus-5-5',
  deadline_seconds: 90,
  max_cost_usd: 0.5,
  max_input_tokens: 80000,
  primary_output_tokens: 6000,
  recovery_output_tokens: 8000,
};

const MODES = [
  ['claude-haiku-5-5', 'claude-sonnet-5-5', 'Haiku → Sonnet'],
  ['claude-haiku-5-5', 'claude-opus-5-5', 'Haiku → Opus'],
  ['claude-sonnet-5-5', 'claude-opus-5-5', 'Sonnet → Opus'],
];
const LIMITS = [
  ['deadline_seconds', 'Time limit per receipt (seconds)', 1, 180, 'any'],
  ['max_cost_usd', 'Cost budget per receipt (USD)', 0, 5, 'any'],
  ['max_input_tokens', 'Maximum input tokens per attempt', 1000, 100000, 1],
  ['primary_output_tokens', 'Maximum primary output tokens', 256, 16000, 1],
  ['recovery_output_tokens', 'Maximum recovery output tokens', 256, 16000, 1],
];
export const recoveryModeLabel = value => MODES.find(([primary, recovery]) => primary === value.primary_model && recovery === value.recovery_model)?.[2] || 'Unsupported mode';
export const recoveryPolicy = value => ({ ...RECOVERY_DEFAULTS, ...value });
export const recoveryError = value => {
  if (!MODES.some(([primary, recovery]) => primary === value.primary_model && recovery === value.recovery_model)) {
    return 'Select a supported parsing mode.';
  }
  for (const [key, label, min, max] of LIMITS) {
    const number = Number(value[key]);
    if (value[key] === '' || !Number.isFinite(number) || number < min || number > max || (key === 'max_cost_usd' && number === 0) ||
        (key.endsWith('_tokens') && !Number.isInteger(number))) {
      return `${label} must be ${key === 'max_cost_usd' ? 'greater than 0 and at most 5' : `${min}–${max}`}${key.endsWith('_tokens') ? ' whole tokens' : ''}.`;
    }
  }
  return '';
};
export const serializeRecovery = value => ({
  ...value,
  ...Object.fromEntries(LIMITS.map(([key]) => [key, Number(value[key])])),
});

export default function ParsingRecoverySettings({ value, onChange, saving }) {
  const mode = MODES.findIndex(([primary, recovery]) => primary === value.primary_model && recovery === value.recovery_model);
  const error = recoveryError(value);
  return (
    <section className="pt-6 border-t border-gray-200 space-y-4" aria-labelledby="parsing-recovery-heading">
      <div>
        <h4 id="parsing-recovery-heading" className="font-medium text-gray-900">AI parsing recovery</h4>
        <p className="text-sm text-gray-500 mt-1">Use source validation and at most one stronger-model recovery attempt for email bodies and travel documents.</p>
      </div>
      <label className="flex items-center gap-3 text-sm font-medium text-gray-900">
        <input type="checkbox" checked={value.enabled} disabled={saving}
          onChange={e => onChange({ ...value, enabled: e.target.checked })} />
        Enable AI parsing recovery
      </label>
      <p className="text-sm text-gray-600" role="status">
        {value.enabled
          ? `Recovery policy selected: ${MODES[mode]?.[2] || 'unsupported mode'}. Takes effect after Save Changes.`
          : 'Recovery is off. The standard parsing model and fallback settings apply.'}
      </p>
      <label className="block text-sm font-medium text-gray-700">
        Parsing recovery mode
        <select className="mt-2 block w-full rounded-md border border-gray-300 p-2" value={mode} disabled={saving}
          onChange={e => {
            const [primary_model, recovery_model] = MODES[Number(e.target.value)];
            onChange({ ...value, primary_model, recovery_model });
          }}>
          {mode < 0 && <option value={-1} disabled>Unsupported saved mode — choose a supported mode</option>}
          {MODES.map(([, , label], index) => <option key={label} value={index}>{label}</option>)}
        </select>
      </label>
      <p className="text-sm text-gray-500">Haiku → Opus is available for evaluation; its accuracy and cost have not been established. Selecting a mode does not enable it.</p>
      <details>
        <summary className="cursor-pointer text-sm font-medium text-gray-700">Cost, time and token limits</summary>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {LIMITS.map(([key, label, min, max, step]) => (
            <label key={key} className="block text-sm text-gray-700">
              {label}
              <input type="number" min={min} max={max} step={step} value={value[key]} disabled={saving}
                className="mt-1 block w-full rounded-md border border-gray-300 p-2"
                onChange={e => onChange({ ...value, [key]: e.target.value })} />
            </label>
          ))}
        </div>
      </details>
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      <p className="text-sm text-gray-600">The time and conservative model-cost budgets are shared across the email and its attachments. Long sources and schedule changes may go directly to the recovery model. Scans and unverified results go to review.</p>
      <p className="text-sm text-gray-600">This is a global setting. AI parsing must also be enabled in Email Parsing settings. AwardWallet settings are managed separately. Standard fallback and retry controls below apply when recovery is off.</p>
    </section>
  );
}
